import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Grid, FormControl, Select, MenuItem, IconButton, Tooltip, Dialog, DialogTitle, Divider, DialogContent, DialogActions } from '@mui/material';
import { HARDWARE_FIELDS } from './HardwareConstants';
import { Close, CloudUpload, InsertDriveFile, UploadFile } from '@mui/icons-material';

const DeviceHardwareForm = ({ formData, handleFormChange, handleFormSubmit, submitLoading, handleCsvUpload }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    const onFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setOpenDialog(true);
        }
        event.target.value = "";
    };
    const onConfirmUpload = () => {
        handleCsvUpload(selectedFile);
        setOpenDialog(false);
        setSelectedFile(null);
    };

    const isFormIncomplete = !formData.imei || formData.imei.trim() === "" ||
        HARDWARE_FIELDS.some(field => !formData[field.key] || formData[field.key] === "");

    return <Paper elevation={3} sx={{ p: 3, width: '100%', maxWidth: 500 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
                Flash Hardware ID Configuration
            </Typography>

            <Tooltip title="Upload CSV" arrow>
                <IconButton color="primary" component="label" disabled={submitLoading}>
                    <input type="file" hidden accept=".csv" onChange={onFileChange} />
                    <UploadFile />
                </IconButton>
            </Tooltip>
        </Box>
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUpload color="primary" /> Confirm Upload
                <IconButton
                    onClick={() => setOpenDialog(false)}
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 1 }}>
                    <InsertDriveFile sx={{ fontSize: 48, color: 'action.active' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', textAlign: 'center' }}>
                        {selectedFile?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Size: {(selectedFile?.size / 1024).toFixed(2)} KB
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Are you sure you want to process this hardware configuration file?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 1 }}>
                <Button onClick={() => setOpenDialog(false)} color="inherit" variant="outlined">
                    Cancel
                </Button>
                <Button onClick={onConfirmUpload} variant="contained" autoFocus>
                    Confirm & Upload
                </Button>
            </DialogActions>
        </Dialog>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
                label="IMEI List (Comma Separated)"
                name="imei"
                value={formData.imei}
                onChange={handleFormChange}
                required
                fullWidth
                multiline
                minRows={3}
                placeholder="e.g., 123456789012345..."
            />

            <Grid container spacing={2}>
                {HARDWARE_FIELDS.map((field) => (
                    <Grid item xs={12} key={field.key}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="subtitle1" component="label" sx={{ fontWeight: 'bold' }}>
                                    {field.label}:
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <FormControl fullWidth required size="small">
                                    <Select
                                        name={field.key}
                                        value={formData[field.key] || ""}
                                        onChange={handleFormChange}
                                        variant="standard"
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if (!selected || selected === "") {
                                                return <em style={{ color: '#aaa', fontStyle: 'normal' }}>Please select {field.label}</em>;
                                            }
                                            return selected;
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Please select {field.label}</em>
                                        </MenuItem>
                                        {field.options.map((option) => (
                                            <MenuItem key={String(option)} value={option}>
                                                {String(option)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button type="submit" variant="contained" color="primary" size="small" disabled={submitLoading || isFormIncomplete}>
                    {submitLoading ? 'Applying...' : 'Apply'}
                </Button>
            </Box>
        </Box>
    </Paper>
};

export default DeviceHardwareForm;